// src/pages/ClientSignup.js
import React, { useState } from "react";
import {
  UserOutlined,
  LockOutlined,
  PhoneOutlined,
  IdcardOutlined,
  HomeOutlined,
  MailOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { Form, Alert, Card, Col, Row, Typography, message, Upload } from "antd";
import axios from "axios";
import GenericForm from "../components/GenericForm";
import { uploadFile } from "../utils/firebase";
import { getUserFriendlyError } from "../utils/errorHandler";
import "./auth.css";

const { Title, Text } = Typography;

const validations = {
  password: [
    { required: true, message: "Please input password!" },
    { min: 8, message: "Minimum 8 characters" },
    { max: 20, message: "Maximum 20 characters" },
    {
      pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[A-Za-z\d@$!%*?&]{8,}$/,
      message: "Requires uppercase, lowercase, and number",
    },
  ],
  email: [
    { required: false }, // Email is now optional
    { type: "email", message: "Invalid email format" },
  ],
  phone: [
    { required: true, message: "Please input phone number!" },
    {
      pattern: /^260[0-9]{9}$/,
      message: "Phone must start with 260 and be 12 digits (e.g., 260977123456)"
    },
  ],
  nrc: [
    { required: true, message: "NRC number/ID/Passport required!" },
  ],
};

const industryTypes = [
  { value: "Manufacturing", label: "Manufacturing" },
  { value: "Healthcare", label: "Healthcare" },
  { value: "Financial Services", label: "Financial Services" },
  { value: "Retail", label: "Retail" },
  { value: "Agriculture", label: "Agriculture" },
  { value: "Energy", label: "Energy" },
  { value: "Construction", label: "Construction" },
  { value: "Transportation", label: "Transportation" },
  { value: "Information Technology", label: "Information Technology" },
  { value: "Real Estate", label: "Real Estate" },
  { value: "Telecommunications", label: "Telecommunications" },
  { value: "Business", label: "Business" },
  { value: "Education", label: "Education" },
];

const provinceTypes = [
  { value: "Lusaka", label: "Lusaka" },
  { value: "Luapula", label: "Luapula" },
  { value: "Copperbelt", label: "Copperbelt" },
  { value: "Central", label: "Central" },
  { value: "North Western", label: "North Western" },
  { value: "Southern", label: "Southern" },
  { value: "Eastern", label: "Eastern" },
  { value: "Muchinga", label: "Muchinga" },
  { value: "Northern", label: "Northern" },
  { value: "Western", label: "Western" },
];

const employmentTypes = [
  { value: "government", label: "Government Employee" },
  { value: "formal_private", label: "Formal Private" },
  { value: "informal", label: "Informal" },
];

const ClientSignup = () => {
  const [uploading, setUploading] = useState(false);
  const [msg, setMsg] = useState({ type: "", text: "" });
  const [form] = Form.useForm();
  const [borrowerType, setBorrowerType] = useState("individual");

  const formFields = [
    // Borrower Type Selection
    {
      label: "Account Type",
      name: "borrower_type",
      type: "select",
      options: [
        { value: "individual", label: "Individual" },
        { value: "sme", label: "SME (Small/Medium Enterprise)" },
      ],
      rules: [{ required: true, message: "Please select account type!" }],
      initialValue: "individual",
      onChange: (value) => setBorrowerType(value),
    },

    // Individual Fields
    {
      label: "First Name",
      name: "first_name",
      type: "input",
      rules: [{ required: borrowerType === "individual", whitespace: true, message: "Required field" }],
      props: { prefix: <UserOutlined /> },
      hidden: borrowerType === "sme",
    },
    {
      label: "Last Name",
      name: "last_name",
      type: "input",
      rules: [{ required: borrowerType === "individual", whitespace: true, message: "Required field" }],
      props: { prefix: <UserOutlined /> },
      hidden: borrowerType === "sme",
    },

    // SME Fields
    {
      label: "Business Name",
      name: "business_name",
      type: "input",
      rules: [{ required: borrowerType === "sme", message: "Please input business name!" }],
      props: { prefix: <UserOutlined /> },
      hidden: borrowerType === "individual",
    },
    {
      label: "Business Registration Number",
      name: "business_registration_number",
      type: "input",
      rules: [{ required: borrowerType === "sme", message: "Please input business registration number!" }],
      props: { prefix: <IdcardOutlined /> },
      hidden: borrowerType === "individual",
    },
    {
      label: "TPIN (Taxpayer Identification Number)",
      name: "tpin",
      type: "input",
      rules: [{ required: borrowerType === "sme", message: "Please input TPIN!" }],
      props: { prefix: <IdcardOutlined /> },
      hidden: borrowerType === "individual",
    },

    // Common Fields
    {
      label: "Email",
      name: "email",
      type: "input",
      rules: validations.email,
      props: { prefix: <MailOutlined /> },
    },
    {
      label: "Password",
      name: "password",
      type: "password",
      rules: validations.password,
      props: { prefix: <LockOutlined /> },
    },
    {
      label: "Confirm Password",
      name: "confirm_password",
      type: "password",
      rules: [
        ...validations.password,
        ({ getFieldValue }) => ({
          validator(_, value) {
            if (!value || getFieldValue("password") === value) {
              return Promise.resolve();
            }
            return Promise.reject(new Error("Passwords do not match"));
          },
        }),
      ],
      props: { prefix: <LockOutlined /> },
    },
    {
      label: "Phone Number",
      name: "phone",
      type: "input",
      rules: validations.phone,
      props: { prefix: <PhoneOutlined /> },
    },

    // Individual-specific fields
    {
      label: "NRC Number/ID/Passport",
      name: "nrc",
      type: "input",
      rules: [{ required: borrowerType === "individual", message: "NRC/ID/Passport required!" }],
      props: { prefix: <IdcardOutlined /> },
      hidden: borrowerType === "sme",
    },
    {
      label: borrowerType === "sme" ? "Business Physical Address" : "Address",
      name: "address",
      type: "textarea",
      rules: [{ required: true, message: "Address required!" }],
    },
    {
      label: "Province",
      name: "province",
      type: "select",
      options: provinceTypes,
      rules: [{ required: borrowerType === "individual", message: "Please select province!" }],
      hidden: borrowerType === "sme",
    },
    {
      label: "Employment Type",
      name: "employment_type",
      type: "select",
      options: employmentTypes,
      rules: [{ required: borrowerType === "individual", message: "Please select employment type!" }],
      hidden: borrowerType === "sme",
    },

    // Industry (for both)
    {
      label: borrowerType === "sme" ? "Business Industry Sector" : "Industry",
      name: "industry",
      type: "select",
      options: industryTypes,
      rules: [{ required: true, message: "Please select industry!" }],
    },

    // SME Directors
    {
      label: "Directors' Details (Name, ID Number, Phone)",
      name: "directors",
      type: "textarea",
      placeholder: "Enter each director's details on a new line:\nJohn Doe, 123456/78/1, +260971234567\nJane Smith, 987654/32/1, +260977654321",
      rules: [{ required: borrowerType === "sme", message: "Please input directors' details!" }],
      hidden: borrowerType === "individual",
    },

    // Documents
    {
      label: "Identity Documents",
      name: "documents",
      type: "upload",
      props: {
        multiple: true,
        accept: "image/*,.pdf",
        maxCount: 5,
        listType: "picture-card",
        beforeUpload: (file) => {
          const isLt5M = file.size / 1024 / 1024 < 5;
          if (!isLt5M) {
            message.error("File must be smaller than 5MB!");
            return Upload.LIST_IGNORE;
          }
          return false;
        },
      },
    },
  ];

  const handleUpload = async (fileList) => {
    try {
      const urls = await Promise.all(
        fileList.map(async (file) => {
          const url = await uploadFile(file.originFileObj, "user-documents");
          return url;
        })
      );
      return urls;
    } catch (error) {
      message.error("Document upload failed");
      throw error;
    }
  };

  const handleSubmit = async (values) => {
    setUploading(true);
    setMsg({ type: "", text: "" });
    try {
      // Upload documents
      const documents = values.documents?.fileList
        ? await handleUpload(values.documents.fileList)
        : [];

      // Build base payload
      const payload = {
        borrower_type: values.borrower_type,
        email: values.email,
        password: values.password,
        phone: values.phone,
        address: values.address,
        industry: values.industry,
        company: 22,
        role: "ab3c07d3-a225-4b68-8454-96e00116e307",
        status: "active",
        documents: documents,
      };

      // Add type-specific fields
      if (values.borrower_type === "individual") {
        payload.first_name = values.first_name;
        payload.last_name = values.last_name;
        payload.nrc = values.nrc;
        payload.province = values.province;
        payload.employment_type = values.employment_type;
      } else {
        // SME fields
        payload.business_name = values.business_name;
        payload.business_registration_number = values.business_registration_number;
        payload.tpin = values.tpin;
        payload.directors = values.directors;

        // For SME, use business name as first_name and set last_name to "SME"
        payload.first_name = values.business_name;
        payload.last_name = "SME";
        // Use business registration number as NRC equivalent
        payload.nrc = values.business_registration_number;
      }

      // Send to API
      await axios.post("https://zedlenders.pickmesms.com/users", payload, {
        headers: { "Content-Type": "application/json" },
      });

      setMsg({
        type: "success",
        text: "Signup successful! Please check your email for verification.",
      });
      form.resetFields();
    } catch (error) {
      console.error("Signup error:", error);
      setMsg({
        type: "error",
        text: getUserFriendlyError(error),
      });
    } finally {
      setUploading(false);
    }
  };

  return (
    <Row
      justify="center"
      align="middle"
      style={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        padding: "40px 15px",
      }}
    >
      <Col xs={24} sm={22} md={20} lg={18} xl={14}>
        <Card
          hoverable
          style={{
            borderRadius: 12,
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
            overflow: "hidden",
          }}
          bodyStyle={{ padding: "40px 32px" }}
        >
          <div style={{ textAlign: "center", marginBottom: 32 }}>
            <Title level={2} style={{ marginBottom: 8, color: "#2D3748" }}>
              Client Registration
            </Title>
            <Text type="secondary" style={{ fontSize: 14 }}>
              Create your financial profile
            </Text>
          </div>

          <GenericForm
            form={form}
            fields={formFields}
            onFinish={handleSubmit}
            submitButtonText="Complete Registration"
            submitButtonProps={{
              loading: uploading,
              disabled: uploading,
              loadingText: "Creating your account...",
              style: { marginTop: "20px" },
            }}
            layout="vertical"
          />

          {msg.text && (
            <Alert
              message={msg.text}
              type={msg.type}
              showIcon
              closable
              onClose={() => setMsg({ type: "", text: "" })}
              style={{
                marginTop: 16,
                borderRadius: 8,
                background: msg.type === "success" ? "#f6ffed" : "#fff1f0",
                border: "none",
              }}
            />
          )}
          <div
            className="auth-footer"
            style={{ textAlign: "center", marginTop: 16 }}
          >
            <Text>
              Already registered? <a href="/clientlogin">Sign In</a>
            </Text>
          </div>
        </Card>
      </Col>
    </Row>
  );
};

export default ClientSignup;
